import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api } from '../lib/api';
import { Alert, Field } from '../components/ui';
import { AuthShell } from '../public/PublicShell';

/**
 * Redeeming an invitation.
 *
 * Reachable without a session — the invitee has no account yet, which is the
 * whole point. The token in the URL is the credential.
 *
 * A password is only asked for when the address has no account. Someone who
 * already has one joins with the token alone: it proves they control the invited
 * mailbox, and demanding a password they may have forgotten would block them from
 * joining at all.
 */
export function AcceptInvite({ onAuthenticated }: { onAuthenticated: () => Promise<void> }) {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setPasswordError(null);
    // Same rule the server enforces; checked here so the answer lands on the
    // field instead of arriving as a banner after a round trip.
    if (password.length > 0 && password.length < 12) {
      setPasswordError(`Use at least 12 characters — ${password.length} so far.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.team.acceptInvite(token, password || undefined);
      await onAuthenticated();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not accept invitation';
      // The API tells us a password is required rather than us guessing whether
      // the address already has an account — it is the only side that knows.
      if (message.toLowerCase().includes('password')) {
        setNeedsPassword(true);
        setError(needsPassword ? message : null);
      } else {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthShell crossLink={{ to: '/login', label: 'Log in' }}>
        <h1>Invitation</h1>
        <Alert kind="error">
          This link has no invitation token. Ask whoever invited you to send it again.
        </Alert>
      </AuthShell>
    );
  }

  return (
    <AuthShell crossLink={{ to: '/login', label: 'Log in' }}>
      <h1>Join the team</h1>
      <p className="auth-sub">Set a password and you are in.</p>

      <form onSubmit={submit}>

        {error && <Alert kind="error">{error}</Alert>}

        {needsPassword ? (
          <>
            <p className="subtitle" style={{ marginBottom: 14 }}>
              You are new here — choose a password and you are in.
            </p>
            <Field
              id="invite-password"
              label="Password"
              error={passwordError}
              hint="At least 12 characters."
            >
              {(props) => (
                <input
                  {...props}
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  required
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    if (passwordError) setPasswordError(null);
                  }}
                />
              )}
            </Field>
          </>
        ) : (
          <p className="subtitle" style={{ marginBottom: 14 }}>
            Accepting adds your account to the organization.
          </p>
        )}

        <button className="btn-primary btn-block" disabled={busy}>
          {busy ? 'Joining…' : 'Accept invitation'}
        </button>
      </form>

      <p className="switch">Invitations expire 7 days after they are created and work once.</p>
    </AuthShell>
  );
}
